import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { GLOBAL } from '../global';
import { Observable } from 'rxjs';
import { Tercero } from '../domain/tercero';
import { GenericRequestReports } from '../domain/generic-request-reports';

@Injectable({
  providedIn: 'root'
})
export class TerceroService {

  urlTercero: string;
  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  /**
   * @author Frank Edward Daza González
   * @version 2018-10-09
   * @param httpClient 
   */
  constructor(private httpClient: HttpClient) {
    this.urlTercero = GLOBAL.URL_BASE + 'sicaTercero';
  }

  /**
   * Crea un tercero en el sistema.
   * 
   * @author Frank Edward Daza González
   * @version 2018-10-09
   * @param client 
   */
  saveSicaTercero(client: Tercero): Observable<any> {
    return this.httpClient.post(this.urlTercero + '/saveSicaTercero/', client, this.httpOptions);
  }

  /**
   * Activa la cuenta de un cliente.
   * 
   * @author Frank Edward Daza González
   * @version 2018-10-11
   * @param client
   */
  activateClient(client: Tercero): Observable<any> {
    //console.log('Información del cliente antes de activarlo en el backend: ' + client);
    return this.httpClient.post(this.urlTercero + '/activateClient/', client, this.httpOptions);
  }

  /**
   * Reenvía un enlace de activación de la cuenta, a los clientes registrados.
	 * Inactiva en código de activación de cuenta, asociado anteriormente al cliente registrado.
   * 
   * @author Frank Edward Daza González
   * @version 2018-10-11
   * @param client 
   */
  reenviarEmailActivacionCliente(client: Tercero): Observable<any> {
    return this.httpClient.post(this.urlTercero + '/reenviarEmailActivacionCliente/', client, this.httpOptions);
  }

  /**
	 * Crea un código de activación para la recuperación de la contraseña, 
	 * y envía un email al cliente con el enlace del cambio de contraseña.
	 * 
	 * @author Frank Edward Daza González
	 * @version 2018-10-16
	 * @param client
	 */
  changeClientPasswordEmail(client: Tercero): Observable<any> {
    return this.httpClient.post(this.urlTercero + '/changeClientPasswordEmail/', client, this.httpOptions);
  }

  /**
	 * Recibe la nueva contraseña del cliente para cambiarla.
	 * Inactiva el código de verificación generado anteriormente, para el 
	 * cambio de contraseña.
	 * 
	 * @author Frank Edward Daza González
	 * @version 2018-10-19
	 * @param client
	 */
  changeNewClientPassword(client: Tercero): Observable<any> {
    return this.httpClient.post(this.urlTercero + '/changeNewClientPassword/', client, this.httpOptions);
  }

  /**
   * Realiza el inicio de sesión de los clientes a SENTINEL.
   * 
   * @author Frank Edward Daza González
	 * @version 2018-10-22
   * @param client 
   */
  login(client: Tercero): Observable<any> {
    return this.httpClient.post(GLOBAL.URL_BASE_SENTINEL + 'login/', client, this.httpOptions);
  }

  /**
	 * Crea un código de activación para la recuperación de la contraseña, 
	 * y envía un SMS al cliente con el código para reestablecer la contraseña.
	 * 
	 * @author Frank Edward Daza González
	 * @version 2018-10-23
	 * @param client
	 */
	changeClientPasswordSMS(client: Tercero): Observable<any> {
    return this.httpClient.post(this.urlTercero + '/changeClientPasswordSMS/', client, this.httpOptions);
  }
  
  /**
	 * Recibe la nueva contraseña del cliente para cambiarla.
	 * Inactiva el código de verificación generado anteriormente, para el 
	 * cambio de contraseña.
	 * 
	 * @author Frank Edward Daza González
	 * @version 2018-10-23
	 * @param client
	 */
  changeNewClientPasswordSMS(client: Tercero): Observable<any> {
    return this.httpClient.post(this.urlTercero + '/changeNewClientPasswordSMS/', client, this.httpOptions);
  }

  /**
   * @author Camilo Delgado
   * @param tiidId Llave primaria del tipo de identificación.
   * @param identificacion  Número de identificación.
   */
  getTerceroByTiidIdYIdentificacion(tiidId : number, identificacion : string) :  Observable<any> {
    return this.httpClient.get(this.urlTercero + '/getTerceroByTiidIdYIdentificacion/' + tiidId + "/" + identificacion, this.httpOptions);
  }
  
  /**
   * Busca un cliente por su correo electrónico, y trae la información asociada al tercero activo.
   * 
   * @author Frank Edward Daza González
	 * @version 2018-11-07
   * @param client 
   */
  findClientByEmail(client: Tercero): Observable<any> {
    return this.httpClient.post(this.urlTercero + '/findClientByEmail/', client, this.httpOptions);
  }

  /**
   * Genera el Json Web Token (JWT).
   * 
   * @author Frank Edward Daza González
	 * @version 2018-11-07
   * @param client 
   */
  generateLoginToken(client: Tercero): Observable<any> {
    //console.log(this.urlTercero);
    return this.httpClient.post(this.urlTercero + '/generateLoginToken/', client, this.httpOptions);
  }

  /**
   * Obtengo el menu del usuario (DIFERENTE A USUARIO FINAL) que ha iniciado sesión
   * @author Camilo Delgado
   * @version Nov 15, 2018
   * @param email
   */
  loadMenu(email: string): Observable<any> {
    let tercero = new Tercero();
    tercero.email = email;
    return this.httpClient.post(this.urlTercero + '/loadMenu/', tercero, this.httpOptions);
  }

  /**
   * Obtengo el menu de los usuarios finales
   * @author Camilo Delgado
   * @version Nov 15, 2018
   */
  loadMenuUsuario(): Observable<any> {
    return this.httpClient.get(this.urlTercero + '/loadMenuUsuario', this.httpOptions);
  }

  /**
   * @author Daniel Pareja Londoño
   * @version Nov 17, 2018
   * @param genericRequestReports 
   */
  reportNewClientsPeriodTime(genericRequestReports:GenericRequestReports):Observable<any>{
    return this.httpClient.post(this.urlTercero + '/reportNewClientsPeriodTime', genericRequestReports, this.httpOptions);
  }

  /**
   * Actualiza la información de un cliente ingresada.
   * 
   * @author Daniel Pareja Londoño
   * @version 2018-12-12
   * @param client 
   */
  updateSicaTerceroClient(client: Tercero): Observable<any> {
    return this.httpClient.post(this.urlTercero + '/updateSicaTerceroClient', client, this.httpOptions);
  }

}
